import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, map, switchMap, tap } from 'rxjs';
import { FormioAppConfig, UserService } from 'dp-formio';

import { KeycloakService } from 'keycloak-angular';
import { _getAccountRolesType } from 'src/formio-custom-components/src/lib/shared/utility/user';
import { CriterionAddMembersModel, ICriterion } from '../models/criterion';
import { IMember } from '../models/member';
import { ISession } from '../models/session';
import { IVisitLite } from '../models/visit';
@Injectable()
export class DistributeService {
  private baseUrl: string;
  private getMembersPath: string;
  private getVisitInfoPath: string;
  private getCriterionPath: string;
  private getSessionsPath: string;
  private getSessionMemberPath: string;
  private updateSessionMembersPath: string;
  private addCriterionMembersPath: string;
  private deleteCriterionMembersPath: string;
  private _visitMembersSubs = new BehaviorSubject<IMember[]>([]);

  constructor(
    private formioAppConfig: FormioAppConfig,
    private userService: UserService,
    private keycloakService: KeycloakService,
    protected http: HttpClient
  ) {
    this._prepareURLS();
  }

  getUserType() {
    return from(this.keycloakService.getToken()).pipe(
      switchMap((token) => this.userService.getUserData(token)),
      map((data) => _getAccountRolesType(data?.user_roles ?? []))
    );
  }

  getVisitInfo(key: string): Observable<IVisitLite> {
    const url = this.baseUrl + this.getVisitInfoPath + key;
    return this.http.get<any>(url);
  }

  getMembersByVisitKey(visitKey: string, skipCache = false): Observable<IMember[]> {
    const url = this.baseUrl + this.getMembersPath + visitKey;
    if (this._visitMembersSubs.getValue()?.length && !skipCache) {
      return this._visitMembersSubs.asObservable();
    }

    return this.http
      .get<any>(url, {
        params: {
          page: '0',
          size: `${Number.MAX_SAFE_INTEGER}`,
        },
      })
      .pipe(
        tap((data) => {
          this._visitMembersSubs.next(data);
        })
      );
  }


  getCriterionByVisitKey(visitKey: string): Observable<ICriterion[]> {
    const url = this.baseUrl + this.getCriterionPath + visitKey + '/tree';
    return this.http.get<any>(url, {
      params: {
        page: '0',
        size: `${Number.MAX_SAFE_INTEGER}`,
      },
    });
  }

  getMyCriterionByVisitKey(visitKey: string, reviewerId): Observable<ICriterion[]> {
    const url = `${this.baseUrl}${this.getCriterionPath}${visitKey}/members/reviewer/${reviewerId}`;
    return this.http.get<any>(url, {
      params: {
        page: '0',
        size: `${Number.MAX_SAFE_INTEGER}`,
      },
    });
  }

  getSessionsByVisitKey(visitKey: string): Observable<ISession[]> {
    const url = this.baseUrl + this.getSessionsPath + visitKey;
    return this.http.get<any>(url, {
      params: {
        page: '0',
        size: `${Number.MAX_SAFE_INTEGER}`,
      },
    });
  }

  updateSessionMembers(session: ISession): Observable<ISession> {
    const url = this.baseUrl + this.updateSessionMembersPath;
    return this.http.put<any>(url, session);
  }

  addCriterionMembers(
    body: CriterionAddMembersModel
  ): Observable<CriterionAddMembersModel> {
    const url = this.baseUrl + this.addCriterionMembersPath;
    return this.http.post<any>(url, body);
  }

  deleteCriterionMembers(body: CriterionAddMembersModel) {
    const url = this.baseUrl + this.deleteCriterionMembersPath;
    return this.http.request('delete', url, { body: body });
  }

  private _prepareURLS() {
    this.baseUrl = this.formioAppConfig.gateway;

    const endpoints = this.formioAppConfig.endpoints;
    this.getVisitInfoPath = endpoints?.['visits'] + 'visit-key/';
    this.getMembersPath = endpoints?.['visitMembers'] + 'visit/';
    this.getCriterionPath = endpoints?.['criterion'] + 'visit/';
    this.getSessionsPath = endpoints?.['visitSessions'] + 'visit/';
    this.getSessionMemberPath = endpoints?.['sessionsMembers'] + 'visit/';
    this.updateSessionMembersPath = endpoints?.['visitSessions'] + 'members';
    this.addCriterionMembersPath = endpoints?.['criterion'] + 'members/add/';
    this.deleteCriterionMembersPath = endpoints?.['criterion'] + 'members/delete/';
  }
}
